/**
 * @file
 * Qlik Fallback behavior.
 *
 * For each iframe pointing to the Qlik origin:
 *   1. Covers it with a loading skeleton and hides the iframe (visibility:hidden
 *      on the clip wrapper, so it keeps loading but never paints over the
 *      skeleton — WebKit renders iframes above siblings regardless of z-index).
 *   2. Waits for a postMessage from Qlik:
 *      - `qlik:ready`  -> Qlik emits this prematurely (before the engine
 *        connection is confirmed), so we DON'T reveal immediately: we hold the
 *        skeleton for REVEAL_GRACE_MS. If a qlik:error arrives in that window we
 *        go straight to the fallback without ever showing the iframe (no flash).
 *        Otherwise we reveal and keep listening for the page lifetime, so a
 *        `qlik:error` at any later point (e.g. session expiry) still flips us to
 *        the fallback. A `qlik:error` always wins, in any state.
 *      - `qlik:error`  -> hides the iframe, shows the fallback modal. Except
 *        errors known to be harmless (see isNonFatalError()), which are ignored.
 *   3. If nothing arrives before READY_TIMEOUT_MS, treats it as a failure
 *      (typical third-party-cookie block, where Qlik never posts anything)
 *      and shows the fallback modal.
 *
 * NOTE: catching the failure requires Qlik to actually emit `qlik:error`. From
 * the parent we cannot see the iframe's failure (cross-origin).
 *
 * The fallback modal markup comes from the <template class="qlik-fallback-template">
 * emitted by the qlik-fallback SDC component.
 *
 * Testing tools (only active on pre/desa/local — see flagsAllowed(); ignored in
 * production):
 *   - `?qlik-debug=1` forces a short timeout (DEBUG_TIMEOUT_MS) so the
 *     skeleton -> modal transition can be checked without a real failure.
 *   - `?qlik-log=1` logs every Qlik message and state transition (no timing
 *     change), for diagnosing what actually arrives from Qlik.
 */

(function (Drupal, once) {
  'use strict';

  const QLIK_ORIGIN = 'https://qap.canariasdestino.com';
  // Dashboards can take ~20s to render; keep margin over that.
  const READY_TIMEOUT_MS = 60000;
  const DEBUG_TIMEOUT_MS = 3000;
  // Qlik emits `ready` prematurely (before the engine connection is confirmed).
  // Wait this long after `ready` before revealing the iframe: if a qlik:error
  // arrives within this window we go straight to the fallback without ever
  // showing the iframe (no flash of Qlik's loader/error). Covers the 3PC case
  // (403 csrftoken + websocket close), which fires within ~1-3s.
  const REVEAL_GRACE_MS = 3000;
  // After revealing, keep listening this long for a late qlik:error (e.g. a
  // session error that surfaces just after) before fully trusting the dashboard.
  const POST_READY_WATCH_MS = 12000;
  const DEBUG_FLAG = 'qlik-debug';
  // Diagnostic logging (does NOT alter timing, unlike debug): logs every Qlik
  // message received and every state transition. Enable with ?qlik-log=1.
  const LOG_FLAG = 'qlik-log';
  // The debug/log flags only work on these non-production hosts (default-off:
  // any other host, including production, ignores the flags).
  const NON_PROD_HOSTS = [
    'pre.observatorioturisticodecanarias.com',
    'desa.observatorioturisticodecanarias.com',
  ];
  // TODO: replace with the real guide URL once content is provided.
  const GUIDE_URL = '#qlik-cookies-guide';

  // True only on pre/desa or local dev, so the diagnostic flags are never
  // accessible in production.
  function flagsAllowed() {
    const host = window.location.hostname;
    return NON_PROD_HOSTS.indexOf(host) !== -1 ||
      /\.ddev\.site$/.test(host) ||
      host === 'localhost';
  }

  function hasFlag(name) {
    if (!flagsAllowed()) return false;
    try {
      return new URLSearchParams(window.location.search).get(name) === '1';
    } catch (e) {
      return false;
    }
  }

  function isDebugEnabled() {
    return hasFlag(DEBUG_FLAG);
  }

  function qlog() {
    if (!hasFlag(LOG_FLAG)) return;
    const args = Array.prototype.slice.call(arguments);
    // eslint-disable-next-line no-console
    console.log.apply(console, ['[qlik-fallback]'].concat(args));
  }

  // A single engine call rejected by Qlik's security rules (e.g. the Sense
  // client trying to SetProperties on a published chart as the anonymous user)
  // surfaces as a capability_api_error "Access denied", but the connection and
  // the dashboard keep working, so it must not trigger the fallback. Real
  // connection failures (third-party cookies blocked, session expiry) arrive as
  // websocket_error / websocket_closed / csrf_403 or a different capability
  // error (e.g. ErrorDialog.NoConnectionCreated).
  function isNonFatalError(data) {
    return data.reason === 'capability_api_error' && data.detail === 'Access denied';
  }

  function getTemplateNode(selector) {
    const tpl = document.querySelector(selector);
    return tpl && tpl.content ? tpl.content.firstElementChild : null;
  }

  // Guarantee a tight, position:relative host wrapping just the iframe, so the
  // skeleton/modal overlay aligns to the iframe (some embeds come without one).
  function ensureWrapper(iframe) {
    let wrapper = iframe.closest('.iframe-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'iframe-wrapper';
      iframe.parentNode.insertBefore(wrapper, iframe);
      wrapper.appendChild(iframe);
    }
    return wrapper;
  }

  // Wrap the iframe in an overflow:hidden, rounded clip container. Fixes the
  // Safari bug where border-radius on an iframe does not clip its content.
  function ensureClip(iframe) {
    let clip = iframe.closest('.qlik-iframe-clip');
    if (!clip) {
      clip = document.createElement('div');
      clip.className = 'qlik-iframe-clip';
      iframe.parentNode.insertBefore(clip, iframe);
      clip.appendChild(iframe);
    }
    return clip;
  }

  function wireButtons(modal, iframe) {
    modal.querySelectorAll('[data-qlik-action]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        switch (btn.dataset.qlikAction) {
          case 'close':
            modal.hidden = true;
            break;
          case 'guide':
            window.open(GUIDE_URL, '_blank', 'noopener');
            break;
          case 'reload':
            window.location.reload();
            break;
        }
      });
    });
  }

  function setupIframe(iframe, modalTemplate, skeletonTemplate) {
    const wrapper = ensureWrapper(iframe);
    const clip = ensureClip(iframe);

    // Fallback modal (hidden until failure).
    const modal = modalTemplate.cloneNode(true);
    wrapper.appendChild(modal);
    wireButtons(modal, iframe);

    // Loading skeleton over the iframe.
    const skeleton = skeletonTemplate.cloneNode(true);
    wrapper.classList.add('qlik-loading');
    wrapper.appendChild(skeleton);
    // Hide the iframe with visibility (not opacity): it keeps loading/running
    // JS so Qlik can still emit qlik:ready, but it never paints over our
    // skeleton (WebKit renders iframes above siblings regardless of z-index).
    clip.classList.add('qlik-iframe-clip--loading');

    let state = 'loading'; // 'loading' | 'ready-pending' | 'ready' | 'failed'
    let timer = null;
    let revealTimer = null;
    let postReadyTimer = null;

    function stopListening() {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
      window.clearTimeout(revealTimer);
      window.clearTimeout(postReadyTimer);
    }

    // Post-ready window elapsed with no error: trust the dashboard for now, but
    // KEEP listening so a later qlik:error (e.g. session expiry) still flips us
    // to the fallback. We keep the (hidden) skeleton in the DOM so a late fail()
    // can reuse it as the backdrop instead of leaving the modal without one.
    function commitReady() {
      window.clearTimeout(timer);
      window.clearTimeout(revealTimer);
      window.clearTimeout(postReadyTimer);
      wrapper.classList.remove('qlik-loading');
    }

    // Grace window after `ready` elapsed with no error: actually show the iframe.
    function commitReveal() {
      if (state === 'failed') return;
      qlog('grace elapsed with no error -> revealing dashboard');
      state = 'ready';
      clip.classList.remove('qlik-iframe-clip--loading');
      clip.classList.add('qlik-iframe-clip--ready');
      // Keep (but hide) the skeleton so we can restore it if a late error comes.
      skeleton.classList.add('qlik-skeleton--hidden');
      postReadyTimer = window.setTimeout(commitReady, POST_READY_WATCH_MS);
    }

    function reveal() {
      // Debug mode forces the fallback path so the popup/skeleton can be tested
      // without a real failure.
      if (state !== 'loading' || isDebugEnabled()) return;
      qlog('qlik:ready -> holding skeleton ' + REVEAL_GRACE_MS + 'ms (grace) before reveal');
      state = 'ready-pending';
      window.clearTimeout(timer);
      // Hold the skeleton up during the grace window; if qlik:error arrives we
      // never reveal the iframe (no flash). Otherwise reveal when it elapses.
      revealTimer = window.setTimeout(commitReveal, REVEAL_GRACE_MS);
    }

    function fail(cause) {
      if (state === 'failed') return;
      qlog('FAIL (' + (cause || 'unknown') + ') from state "' + state + '" -> showing popup');
      state = 'failed';
      stopListening();
      // Lock the current height so removing the iframe doesn't collapse the box.
      wrapper.style.minHeight = wrapper.offsetHeight + 'px';
      // Remove the iframe entirely so Qlik's own error screen can never
      // show through, and turn the skeleton into a clean static backdrop behind
      // the modal (works whether or not we had already revealed).
      clip.style.display = 'none';
      skeleton.classList.remove('qlik-skeleton--hidden');
      skeleton.classList.add('qlik-skeleton--error');
      modal.hidden = false;
    }

    function onMessage(event) {
      if (event.origin !== QLIK_ORIGIN) return;
      const data = event.data || {};
      // Log every message from the Qlik origin, including ones we filter out, so
      // we can see if qlik:error arrives but is rejected (wrong source, etc.).
      qlog('message from', event.origin, '| type=', data.type,
        '| sameIframe=', event.source === iframe.contentWindow, '| data=', data);
      if (event.source !== iframe.contentWindow) return;
      if (data.type === 'qlik:ready') {
        reveal();
      } else if (data.type === 'qlik:error') {
        if (isNonFatalError(data)) {
          qlog('ignoring non-fatal qlik:error (' + data.reason + ': ' + data.detail + ')');
          return;
        }
        fail('qlik:error ' + data.reason);
      }
    }

    window.addEventListener('message', onMessage);
    timer = window.setTimeout(function () {
      fail('timeout ' + (isDebugEnabled() ? DEBUG_TIMEOUT_MS : READY_TIMEOUT_MS) + 'ms');
    }, isDebugEnabled() ? DEBUG_TIMEOUT_MS : READY_TIMEOUT_MS);
  }

  Drupal.behaviors.qlikFallback = {
    attach: function (context) {
      const modalTemplate = getTemplateNode('.qlik-fallback-template');
      const skeletonTemplate = getTemplateNode('.qlik-skeleton-template');
      if (!modalTemplate || !skeletonTemplate) return;

      const selector = 'iframe[src^="' + QLIK_ORIGIN + '"]';
      once('qlik-fallback', selector, context).forEach(function (iframe) {
        setupIframe(iframe, modalTemplate, skeletonTemplate);
      });
    }
  };

})(Drupal, once);
